import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../contexts/NavigationContext';
import * as api from '../services/api';
import {
  AlertCircle, ChevronDown, ChevronUp, Plus, Search, Filter, PlayCircle,
  CheckCircle, Clock, AlertTriangle, Eye, Trash2, Lock, MoreVertical,
  RefreshCw, Download, Edit, ToggleLeft, Settings, Shield, ShieldAlert,
  Activity, Zap, Radio, Network, GitBranch, Cpu, Key, Fingerprint,
  Layers, Target, TrendingUp, Radar, Inbox, Bot, Globe2, Wifi,
  Loader, X, ChevronRight, ArrowUpRight, ScanLine,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
/* G100 — full v2 redesign of ShadowDiscoveryPage. Replaces 100vh
 * flex layout + gradient tab bar with V2PageShell + V2TabNav.
 * v1 hooks/handlers/api calls preserved verbatim. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Pill as V2Pill,
} from '../components/v2';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Credo.ai-inspired Enterprise Governance Aesthetic
   Matches PolicyConfigPage: Stone palette, ACCENT semantics, shared components
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = { // Stone palette
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const STATUS_MAP = {
  NEW:            { bg: ACCENT.sky.bg,     text: ACCENT.sky.text,     border: ACCENT.sky.border,     label: 'New',            icon: Inbox,       accent: ACCENT.sky },
  INVESTIGATING:  { bg: ACCENT.amber.bg,   text: ACCENT.amber.text,   border: ACCENT.amber.border,   label: 'Investigating',  icon: Eye,         accent: ACCENT.amber },
  CONFIRMED:      { bg: ACCENT.violet.bg,  text: ACCENT.violet.text,  border: ACCENT.violet.border,  label: 'Confirmed',      icon: CheckCircle, accent: ACCENT.violet },
  ENROLLED:       { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, label: 'Enrolled',       icon: Shield,      accent: ACCENT.emerald },
  DISMISSED:      { bg: S[100],            text: S[500],              border: S[200],                label: 'Dismissed',      icon: X,           accent: ACCENT.slate },
  FALSE_POSITIVE: { bg: S[100],            text: S[500],              border: S[200],                label: 'False Positive', icon: X,           accent: ACCENT.slate },
};

const RISK_MAP = {
  CRITICAL: { bg: ACCENT.rose.bg,    text: ACCENT.rose.text,    border: ACCENT.rose.border,    icon: ACCENT.rose.icon,    accent: ACCENT.rose },
  HIGH:     { bg: ACCENT.amber.bg,   text: ACCENT.amber.text,   border: ACCENT.amber.border,   icon: ACCENT.amber.icon,   accent: ACCENT.amber },
  MEDIUM:   { bg: ACCENT.violet.bg,  text: ACCENT.violet.text,  border: ACCENT.violet.border,  icon: ACCENT.violet.icon,  accent: ACCENT.violet },
  LOW:      { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, icon: ACCENT.emerald.icon, accent: ACCENT.emerald },
};

const ENGINE_META = {
  NETWORK_INTELLIGENCE:    { label: 'Network Intelligence',  icon: Wifi,        accent: ACCENT.sky },
  MCP_PROTOCOL:            { label: 'MCP / A2A Protocol',    icon: Network,     accent: ACCENT.violet },
  CLOUD_PLATFORM:          { label: 'Cloud Platform',        icon: Globe2,      accent: ACCENT.amber },
  CODE_REPOSITORY:         { label: 'Code Repository',       icon: GitBranch,   accent: ACCENT.emerald },
  IDENTITY_FORENSICS:      { label: 'Identity Forensics',    icon: Key,         accent: ACCENT.rose },
  BEHAVIORAL_FINGERPRINT:  { label: 'Behavioral Fingerprint', icon: Fingerprint, accent: ACCENT.slate },
  GOVERNANCE_GAP:          { label: 'Protection Gap',        icon: Layers,      accent: ACCENT.amber },
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard',           icon: Activity,   desc: 'Real-time shadow agent posture and governance gap overview' },
  { key: 'agents',    label: 'Discovered Agents',   icon: Bot,        desc: 'Triage, investigate, and enroll shadow agents' },
  { key: 'graph',     label: 'Communication Graph', icon: Network,    desc: 'Agent-to-agent communication topology and data flows' },
  { key: 'scans',     label: 'Scans & Rules',       icon: Radar,      desc: 'Discovery engine execution history and detection rules' },
  { key: 'signals',   label: 'Signals',             icon: Radio,      desc: 'Raw detection signals from all discovery engines' },
];

/* ─── Shared Base Styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};

/* ─── CSS Keyframes (injected once) ─────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @keyframes sdpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes sdpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes sdpSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes sdpShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes sdpFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .sdp-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
    .sdp-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .sdp-btn-ghost:hover { background: ${S[100]} !important; }
    .sdp-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
    .sdp-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .sdp-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .sdp-search:focus { border-color: ${BRAND.primary}60 !important; box-shadow: 0 0 0 3px ${BRAND.glow} !important; }
    .sdp-row:hover { background: ${S[25]} !important; }
    .sdp-action-btn:hover { background: ${S[150]} !important; color: ${S[800]} !important; }
    .sdp-engine-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important; transform: translateY(-1px); }
    .sdp-engine-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .sdp-signal-card:hover { border-color: ${S[300]} !important; background: ${S[25]} !important; }
    .sdp-signal-card { transition: all 0.15s ease !important; }
  `}</style>
);

/* ══════════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function ShimmerBlock({ width = '100%', height = 28 }) {
  return (
    <div style={{
      width, height, borderRadius: 6,
      background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
      backgroundSize: '200% 100%', animation: 'sdpShimmer 1.5s infinite',
    }} />
  );
}

function StatusBadge({ status, size = 'default' }) {
  const s = STATUS_MAP[status] || STATUS_MAP.NEW;
  const Icon = s.icon;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: s.bg, color: s.text,
      border: `1px solid ${s.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <Icon size={isSmall ? 10 : 11} />
      {s.label}
    </span>
  );
}

function RiskBadge({ tier, size = 'default' }) {
  const r = RISK_MAP[tier] || RISK_MAP.MEDIUM;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: r.bg, color: r.text,
      border: `1px solid ${r.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <ShieldAlert size={isSmall ? 10 : 11} />
      {tier}
    </span>
  );
}

function MetricPill({ value, label, icon: Icon, trend }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, color: S[500], background: S[50], padding: '3px 10px',
      borderRadius: 6, border: `1px solid ${S[200]}`,
    }}>
      {Icon && <Icon size={11} color={S[400]} />}
      <strong style={{ color: S[700], fontWeight: 600 }}>{value}</strong>
      {label && <span>{label}</span>}
      {trend && <TrendingUp size={10} color={ACCENT.emerald.icon} />}
    </span>
  );
}

/* ── Stat Card (Hero Row) ───────────────────────────────────────────────── */
function StatCard({ label, value, subtitle, icon: Icon, accent, loading: isLoading }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="sdp-stat-card" style={{
      flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: 'default', transition: 'all 0.2s ease',
    }}>
      {/* Accent top line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <ShimmerBlock width={48} height={28} />
          ) : (
            <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{value}</div>
          )}
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 6, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

/* ── Section Card (white panel wrapper) ───────────────────────────────── */
function SectionCard({ title, accent, actions, children }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="sdp-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.12 }} />
      <div style={{ padding: '18px 20px' }}>
        {(title || actions) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            {title && <div style={{ fontSize: 13, fontWeight: 700, color: S[800], letterSpacing: '-0.01em' }}>{title}</div>}
            {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ── Loading Skeleton ─────────────────────────────────────────────────── */
function TabSkeleton() {
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'sdpFadeIn 0.3s ease' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
            borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: S[200] }} />
            <ShimmerBlock width={80} height={10} />
            <div style={{ marginTop: 12 }}><ShimmerBlock width={48} height={28} /></div>
            <div style={{ marginTop: 8 }}><ShimmerBlock width={120} height={10} /></div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: 20 }}>
        <ShimmerBlock width={160} height={14} />
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3, 4, 5].map(i => <ShimmerBlock key={i} height={44} />)}
        </div>
      </div>
    </div>
  );
}

/* ── Empty State ──────────────────────────────────────────────────────── */
function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{
      padding: 48, textAlign: 'center', background: S[50], borderRadius: 12,
      border: `1px dashed ${S[200]}`, margin: 24, animation: 'sdpFadeIn 0.3s ease',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, background: S[100], border: `1px solid ${S[200]}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
      }}>
        <Icon size={22} color={S[400]} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: S[800], marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: S[500], maxWidth: 360, margin: '0 auto' }}>{subtitle}</div>
    </div>
  );
}

/* ── Batch Action Bar ─────────────────────────────────────────────────── */
const BatchActionBar = ({ selectedCount, onBulkStatusChange, onBulkDismiss, onCancel }) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      display: 'flex', alignItems: 'center', gap: 12,
      background: '#fff', border: `1px solid ${BRAND.primary}40`, borderRadius: 12,
      padding: '12px 20px', boxShadow: `0 8px 32px ${BRAND.glow}, 0 2px 8px rgba(0,0,0,0.08)`, zIndex: 100,
      animation: 'sdpSlideUp 0.2s ease',
    }}>
      <span style={{
        fontSize: 12, fontWeight: 700, color: BRAND.primary,
        fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      }}>
        {selectedCount} selected
      </span>

      <div style={{ width: 1, height: 24, background: S[200] }} />

      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setStatusMenuOpen(!statusMenuOpen)}
          style={{
            ...btnBase, padding: '8px 12px', border: `1px solid ${S[200]}`,
            background: '#fff', fontSize: 13, fontWeight: 500, color: S[700],
          }}
        >
          Update Status
          <ChevronDown size={14} />
        </button>
        {statusMenuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 8,
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)', minWidth: 180, zIndex: 1001,
            overflow: 'hidden',
          }}>
            {['INVESTIGATING', 'CONFIRMED', 'ENROLLED'].map((status) => {
              const sm = STATUS_MAP[status];
              return (
                <button
                  key={status}
                  onClick={() => { onBulkStatusChange(status); setStatusMenuOpen(false); }}
                  className="sdp-row"
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: 8,
                    textAlign: 'left', padding: '10px 14px', border: 'none',
                    background: 'transparent', cursor: 'pointer', fontSize: 13, color: S[700],
                    transition: 'background 0.15s',
                  }}
                >
                  <sm.icon size={13} color={sm.accent.icon} />
                  {sm.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={onBulkDismiss}
        className="sdp-btn-ghost"
        style={{ ...btnBase, padding: '8px 12px', border: `1px solid ${S[200]}`, background: '#fff', fontSize: 13, fontWeight: 500, color: S[600] }}
      >
        Dismiss All
      </button>

      <button
        onClick={onCancel}
        style={{ ...btnBase, padding: '8px 12px', background: S[100], fontSize: 13, fontWeight: 500, color: S[500] }}
      >
        Cancel
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   TAB PANELS
   ══════════════════════════════════════════════════════════════════════════════ */

const DashboardTab = ({ loading, data, onRunScan }) => {
  if (loading) return <TabSkeleton />;

  const dashboard = data || {};
  const metrics = dashboard.metrics || {};

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'sdpFadeIn 0.3s ease' }}>
      {/* Hero Stat Row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard label="Critical Risk" value={metrics.critical || 0} subtitle="Immediate protection required" icon={ShieldAlert} accent={ACCENT.rose} />
        <StatCard label="High Risk" value={metrics.high || 0} subtitle="Investigate within 24h" icon={AlertTriangle} accent={ACCENT.amber} />
        <StatCard label="Medium Risk" value={metrics.medium || 0} subtitle="Schedule for review" icon={Eye} accent={ACCENT.violet} />
        <StatCard label="Low Risk" value={metrics.low || 0} subtitle="Monitor passively" icon={Shield} accent={ACCENT.emerald} />
      </div>

      {/* Governance Gap Score */}
      <SectionCard title="Protection Gap Score" accent={ACCENT.rose} actions={
        <button
          onClick={onRunScan}
          className="sdp-btn-primary"
          style={{ ...btnBase, padding: '8px 16px', background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600 }}
        >
          <PlayCircle size={14} />
          Run Full Scan
        </button>
      }>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
            <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="60" cy="60" r="50" fill="none" stroke={S[150]} strokeWidth="8" />
              <circle
                cx="60" cy="60" r="50" fill="none" stroke={BRAND.primary}
                strokeWidth="8" strokeDasharray={`${3.14 * 50 * (metrics.gapScore || 0) / 100} ${3.14 * 100}`}
                strokeLinecap="round"
              />
            </svg>
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: 24, fontWeight: 800, color: S[900], letterSpacing: '-0.03em',
                fontFeatureSettings: "'tnum'",
              }}>
                {metrics.gapScore || 0}%
              </div>
              <div style={{ fontSize: 10, color: S[500], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coverage</div>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, color: S[600], margin: 0, marginBottom: 12, lineHeight: 1.6 }}>
              Your organization has a <strong style={{ color: S[800] }}>{metrics.gapScore || 0}%</strong> protection
              coverage gap. Discovery engines have identified <strong style={{ color: S[800] }}>{metrics.totalDiscovered || 0}</strong> shadow
              agents that require protection policies.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <MetricPill value={metrics.totalDiscovered || 0} label="discovered" icon={Radar} />
              <MetricPill value={metrics.totalEnrolled || 0} label="enrolled" icon={Shield} />
              <MetricPill value={metrics.totalDismissed || 0} label="dismissed" icon={X} />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Recent Discoveries */}
      <SectionCard title="Recent Discoveries" accent={ACCENT.sky}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(dashboard.recentDiscoveries || []).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 13 }}>No recent discoveries</div>
          ) : (
            (dashboard.recentDiscoveries || []).slice(0, 10).map((agent, i) => (
              <div key={i} className="sdp-row" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: S[25], borderRadius: 8, fontSize: 13,
                transition: 'background 0.15s', cursor: 'default',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: (RISK_MAP[agent.riskTier] || RISK_MAP.MEDIUM).bg,
                    border: `1px solid ${(RISK_MAP[agent.riskTier] || RISK_MAP.MEDIUM).border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Bot size={14} color={(RISK_MAP[agent.riskTier] || RISK_MAP.MEDIUM).icon} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: S[800] }}>{agent.name}</div>
                    <div style={{ fontSize: 11, color: S[500] }}>
                      {agent.platform} · {agent.type}
                    </div>
                  </div>
                </div>
                <RiskBadge tier={agent.riskTier} size="small" />
              </div>
            ))
          )}
        </div>
      </SectionCard>

      {/* Engine Status Grid */}
      <SectionCard title="Engine Status" accent={ACCENT.violet}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {(dashboard.engineStatus || []).map((engine) => {
            const meta = ENGINE_META[engine.name] || { label: engine.name, icon: Cpu, accent: ACCENT.slate };
            const EngineIcon = meta.icon;
            const isHealthy = engine.status === 'HEALTHY';
            return (
              <div key={engine.name} className="sdp-engine-card" style={{
                padding: 14, background: S[25], borderRadius: 10, border: `1px solid ${S[200]}`,
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: meta.accent.icon, opacity: 0.15 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EngineIcon size={14} color={meta.accent.icon} />
                    <div style={{ fontSize: 12, fontWeight: 600, color: S[800] }}>{meta.label}</div>
                  </div>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: isHealthy ? ACCENT.emerald.icon : engine.status === 'WARNING' ? ACCENT.amber.icon : ACCENT.rose.icon,
                  }} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <MetricPill value={engine.agentsFound} label="found" icon={Bot} />
                  <MetricPill value={engine.signalsGenerated} label="signals" icon={Radio} />
                </div>
                <div style={{
                  fontSize: 10, color: S[400], marginTop: 8,
                  fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                }}>
                  Last: {engine.lastScan}
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
};

const AgentsTab = ({ loading, data, selectedAgents, onSelectAgent, onInvestigate, onEnroll, onDismiss, onBulkStatusChange, onBulkDismiss }) => {
  const [filter, setFilter] = useState({ status: 'All', riskTier: 'All', search: '' });
  const [sortBy, setSortBy] = useState('firstSeen');
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  if (loading) return <TabSkeleton />;

  const agents = data || [];

  if (agents.length === 0) {
    return <EmptyState icon={Bot} title="No shadow agents discovered yet" subtitle="Run your first scan to discover agents operating outside protection" />;
  }

  let filtered = agents.filter((a) => {
    if (filter.status !== 'All' && a.status !== filter.status) return false;
    if (filter.riskTier !== 'All' && a.riskTier !== filter.riskTier) return false;
    if (filter.search && !a.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  filtered.sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return sortDesc ? -cmp : cmp;
  });

  const pageSize = 20;
  const pageData = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, animation: 'sdpFadeIn 0.3s ease' }}>
      {/* Filter Bar */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#fff', borderRadius: 10, border: `1px solid ${S[200]}`,
      }}>
        <Filter size={15} color={S[400]} />

        <select
          value={filter.status}
          onChange={(e) => { setFilter({ ...filter, status: e.target.value }); setPage(0); }}
          style={{ ...inputBase, width: 'auto', padding: '7px 12px', fontSize: 12 }}
        >
          <option value="All">Status: All</option>
          {Object.keys(STATUS_MAP).map((s) => (
            <option key={s} value={s}>{STATUS_MAP[s].label}</option>
          ))}
        </select>

        <select
          value={filter.riskTier}
          onChange={(e) => { setFilter({ ...filter, riskTier: e.target.value }); setPage(0); }}
          style={{ ...inputBase, width: 'auto', padding: '7px 12px', fontSize: 12 }}
        >
          <option value="All">Risk: All</option>
          {Object.keys(RISK_MAP).map((rt) => (
            <option key={rt} value={rt}>{rt}</option>
          ))}
        </select>

        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 220,
          padding: '7px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
          background: '#fff', transition: 'border-color 0.15s, box-shadow 0.15s',
        }}>
          <Search size={14} color={S[400]} />
          <input
            className="sdp-search"
            placeholder="Search agents..."
            value={filter.search}
            onChange={(e) => { setFilter({ ...filter, search: e.target.value }); setPage(0); }}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: S[900] }}
          />
        </div>

        <MetricPill value={filtered.length} label={`of ${agents.length}`} icon={Bot} />
      </div>

      {/* Data Table */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        overflow: 'hidden', position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: BRAND.primary, opacity: 0.08 }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: S[50], borderBottom: `1px solid ${S[200]}` }}>
            <tr>
              <th style={{ padding: '10px 16px', textAlign: 'left', width: 40 }}>
                <input
                  type="checkbox"
                  checked={selectedAgents.length > 0 && selectedAgents.length === pageData.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectAgent(pageData.map(a => a.id));
                    } else {
                      onSelectAgent(selectedAgents.filter(id => !pageData.find(a => a.id === id)));
                    }
                  }}
                  style={{ cursor: 'pointer', accentColor: BRAND.primary }}
                />
              </th>
              {[
                { key: 'name', label: 'Name' },
                { key: 'risktier', label: 'Risk Tier' },
                { key: 'confidence', label: 'Confidence' },
                { key: 'platform', label: 'Platform' },
                { key: 'agenttype', label: 'Type' },
                { key: 'firstseen', label: 'First Seen' },
                { key: 'status', label: 'Status' },
                { key: null, label: 'Actions' },
              ].map((col) => (
                <th
                  key={col.label}
                  onClick={() => {
                    if (!col.key) return;
                    if (sortBy === col.key) { setSortDesc(!sortDesc); }
                    else { setSortBy(col.key); setSortDesc(true); }
                  }}
                  style={{
                    padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: S[500],
                    cursor: col.key ? 'pointer' : 'default', userSelect: 'none', fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}
                >
                  {col.label}
                  {sortBy === col.key && (sortDesc ? ' ↓' : ' ↑')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((agent) => {
              const isSelected = selectedAgents.includes(agent.id);
              const isExpanded = expandedId === agent.id;
              return (
                <React.Fragment key={agent.id}>
                  <tr
                    className="sdp-row"
                    style={{
                      background: isSelected ? BRAND.light : '#fff',
                      borderBottom: `1px solid ${S[150]}`,
                      cursor: 'pointer', transition: 'background 0.15s',
                    }}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            onSelectAgent(selectedAgents.filter(id => id !== agent.id));
                          } else {
                            onSelectAgent([...selectedAgents, agent.id]);
                          }
                        }}
                        style={{ cursor: 'pointer', accentColor: BRAND.primary }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : agent.id)}
                        style={{
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          color: S[800], fontWeight: 600, display: 'flex',
                          alignItems: 'center', gap: 6, padding: 0, fontSize: 13,
                        }}
                      >
                        {isExpanded ? <ChevronUp size={13} color={S[400]} /> : <ChevronDown size={13} color={S[400]} />}
                        {agent.name}
                      </button>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <RiskBadge tier={agent.riskTier} size="small" />
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          height: 6, background: S[150], borderRadius: 3, overflow: 'hidden', width: 56,
                        }}>
                          <div style={{
                            height: '100%', borderRadius: 3,
                            background: (agent.confidence || 0) >= 80 ? ACCENT.emerald.icon : (agent.confidence || 0) >= 50 ? ACCENT.amber.icon : ACCENT.rose.icon,
                            width: `${agent.confidence || 0}%`, transition: 'width 0.3s ease',
                          }} />
                        </div>
                        <span style={{
                          fontSize: 11, color: S[600], fontWeight: 600,
                          fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                        }}>
                          {agent.confidence || 0}%
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', color: S[700], fontSize: 12 }}>{agent.platform}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 500, color: S[500], background: S[50],
                        padding: '2px 7px', borderRadius: 4, border: `1px solid ${S[150]}`,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>
                        {agent.agentType}
                      </span>
                    </td>
                    <td style={{
                      padding: '10px 16px', color: S[500], fontSize: 11,
                      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                    }}>
                      {agent.firstSeen}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <StatusBadge status={agent.status} size="small" />
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onInvestigate(agent.id); }}
                          title="Investigate"
                          className="sdp-action-btn"
                          style={{
                            ...btnBase, padding: '5px 7px', borderRadius: 6, border: `1px solid ${S[200]}`,
                            background: '#fff', color: S[500],
                          }}
                        >
                          <Eye size={13} />
                        </button>
                        {agent.status !== 'ENROLLED' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onEnroll(agent.id); }}
                            title="Enroll"
                            className="sdp-btn-primary"
                            style={{
                              ...btnBase, padding: '5px 7px', borderRadius: 6, border: 'none',
                              background: BRAND.primary, color: '#fff',
                            }}
                          >
                            <CheckCircle size={13} />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onDismiss(agent.id); }}
                          title="Dismiss"
                          className="sdp-action-btn"
                          style={{
                            ...btnBase, padding: '5px 7px', borderRadius: 6, border: `1px solid ${S[200]}`,
                            background: '#fff', color: S[500],
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: S[50], borderBottom: `1px solid ${S[150]}` }}>
                      <td colSpan="9" style={{ padding: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, animation: 'sdpSlideUp 0.2s ease' }}>
                          <div>
                            <div style={{
                              fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                              letterSpacing: '0.08em', marginBottom: 8,
                            }}>
                              Signals
                            </div>
                            {agent.signals && agent.signals.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {agent.signals.map((sig, i) => (
                                  <div key={i} style={{
                                    padding: '6px 10px', background: '#fff', borderRadius: 6,
                                    border: `1px solid ${S[200]}`, fontSize: 12, color: S[700],
                                    display: 'flex', alignItems: 'center', gap: 8,
                                  }}>
                                    <Radio size={11} color={ACCENT.amber.icon} />
                                    <span style={{ fontWeight: 600 }}>{sig.type}</span>
                                    <span style={{
                                      fontSize: 10, color: S[500],
                                      fontFamily: "'IBM Plex Mono', monospace",
                                    }}>
                                      {sig.confidence}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: S[400] }}>No signals</div>
                            )}
                          </div>
                          <div>
                            <div style={{
                              fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                              letterSpacing: '0.08em', marginBottom: 8,
                            }}>
                              Communication Edges
                            </div>
                            {agent.edges && agent.edges.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {agent.edges.map((edge, i) => (
                                  <div key={i} style={{
                                    padding: '6px 10px', background: '#fff', borderRadius: 6,
                                    border: `1px solid ${S[200]}`, fontSize: 12, color: S[700],
                                    display: 'flex', alignItems: 'center', gap: 8,
                                  }}>
                                    <Network size={11} color={ACCENT.sky.icon} />
                                    <ChevronRight size={11} color={S[300]} />
                                    <span style={{ fontWeight: 600 }}>{edge.targetAgent}</span>
                                    <span style={{
                                      fontSize: 10, color: S[500],
                                      fontFamily: "'IBM Plex Mono', monospace",
                                    }}>
                                      {edge.protocol}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: S[400] }}>No edges</div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', background: '#fff', borderRadius: 8, border: `1px solid ${S[200]}`,
      }}>
        <div style={{
          fontSize: 12, color: S[500],
          fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
        }}>
          {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            className="sdp-btn-ghost"
            style={{
              ...btnBase, padding: '6px 12px', border: `1px solid ${S[200]}`, background: '#fff',
              fontSize: 12, color: S[600], opacity: page === 0 ? 0.4 : 1,
              cursor: page === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            Previous
          </button>
          <div style={{
            padding: '6px 12px', fontSize: 12, color: S[500],
            fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
          }}>
            {page + 1} / {totalPages || 1}
          </div>
          <button
            disabled={(page + 1) * pageSize >= filtered.length}
            onClick={() => setPage(page + 1)}
            className="sdp-btn-ghost"
            style={{
              ...btnBase, padding: '6px 12px', border: `1px solid ${S[200]}`, background: '#fff',
              fontSize: 12, color: S[600],
              opacity: (page + 1) * pageSize >= filtered.length ? 0.4 : 1,
              cursor: (page + 1) * pageSize >= filtered.length ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      </div>

      {selectedAgents.length > 0 && (
        <BatchActionBar
          selectedCount={selectedAgents.length}
          onBulkStatusChange={onBulkStatusChange}
          onBulkDismiss={onBulkDismiss}
          onCancel={() => onSelectAgent([])}
        />
      )}
    </div>
  );
};

const GraphTab = ({ loading, data }) => {
  const [selectedNode, setSelectedNode] = useState(null);

  if (loading) return <TabSkeleton />;

  const nodes = data?.nodes || [];
  const edges = data?.edges || [];

  if (nodes.length === 0 && edges.length === 0) {
    return <EmptyState icon={Network} title="No communication graph data" subtitle="Agent communication edges will appear here after discovery scans detect inter-agent traffic" />;
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'sdpFadeIn 0.3s ease' }}>
      {/* Agent Nodes */}
      <SectionCard title="Agent Nodes" accent={ACCENT.sky} actions={
        <MetricPill value={nodes.length} label="nodes" icon={Bot} />
      }>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {nodes.map((node) => {
            const statusAccent = node.status === 'governed' ? ACCENT.emerald : node.status === 'investigating' ? ACCENT.amber : ACCENT.rose;
            const isActive = selectedNode === node.id;
            return (
              <button
                key={node.id}
                onClick={() => setSelectedNode(isActive ? null : node.id)}
                className="sdp-engine-card"
                style={{
                  padding: 14, borderRadius: 10,
                  border: `1px solid ${isActive ? statusAccent.icon : S[200]}`,
                  background: isActive ? statusAccent.bg : '#fff',
                  cursor: 'pointer', textAlign: 'left', position: 'relative', overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: statusAccent.icon, opacity: 0.2 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Bot size={14} color={statusAccent.icon} />
                  <div style={{ fontSize: 12, fontWeight: 600, color: S[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {node.name}
                  </div>
                </div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                  background: statusAccent.bg, color: statusAccent.text, border: `1px solid ${statusAccent.border}`,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusAccent.icon }} />
                  {node.status}
                </span>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* Edge List */}
      <SectionCard title="Communication Edges" accent={ACCENT.violet} actions={
        <MetricPill value={edges.length} label="edges" icon={Network} />
      }>
        <div style={{
          borderRadius: 8, overflow: 'hidden', border: `1px solid ${S[150]}`,
        }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead style={{ background: S[50] }}>
              <tr>
                {['Source', 'Target', 'Type', 'Protocol', 'Frequency'].map(col => (
                  <th key={col} style={{
                    padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: S[500],
                    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
                    borderBottom: `1px solid ${S[200]}`,
                  }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {edges.map((edge, i) => (
                <tr key={i} className="sdp-row" style={{ borderBottom: i < edges.length - 1 ? `1px solid ${S[150]}` : 'none', transition: 'background 0.15s' }}>
                  <td style={{ padding: '9px 14px', color: S[800], fontWeight: 600 }}>{edge.source}</td>
                  <td style={{ padding: '9px 14px', color: S[800], fontWeight: 600 }}>{edge.target}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 500, color: S[500], background: S[50],
                      padding: '2px 7px', borderRadius: 4, border: `1px solid ${S[150]}`,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}>{edge.type}</span>
                  </td>
                  <td style={{
                    padding: '9px 14px', color: S[600], fontSize: 11,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}>{edge.protocol}</td>
                  <td style={{
                    padding: '9px 14px', color: S[600], fontSize: 11,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}>{edge.frequency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

const ScansRulesTab = ({ loading, data, onRunFullScan, onRunEngineScan, onCreateRule }) => {
  const [expandedEngine, setExpandedEngine] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);

  if (loading) return <TabSkeleton />;

  const scans = data?.scans || [];
  const rules = data?.rules || {};

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'sdpFadeIn 0.3s ease' }}>
      {/* Scan Triggers */}
      <SectionCard title="Discovery Scans" accent={ACCENT.sky} actions={
        <button
          onClick={onRunFullScan}
          className="sdp-btn-primary"
          style={{ ...btnBase, padding: '8px 16px', background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600 }}
        >
          <PlayCircle size={14} />
          Run Full Scan
        </button>
      }>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
          {Object.entries(ENGINE_META).map(([key, meta]) => {
            const EngineIcon = meta.icon;
            return (
              <button
                key={key}
                onClick={() => onRunEngineScan(key)}
                className="sdp-engine-card"
                style={{
                  padding: '12px 14px', borderRadius: 10, border: `1px solid ${meta.accent.border}`,
                  background: meta.accent.bg, cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <EngineIcon size={14} color={meta.accent.icon} />
                <span style={{ fontSize: 12, fontWeight: 600, color: meta.accent.text }}>{meta.label}</span>
              </button>
            );
          })}
        </div>

        {/* Recent Scans Table */}
        <div style={{
          fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
          letterSpacing: '0.08em', marginBottom: 10,
        }}>
          Recent Scans
        </div>
        <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${S[150]}` }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead style={{ background: S[50] }}>
              <tr>
                {['Engine', 'Status', 'Agents Found', 'Signals', 'Duration', 'Date'].map(col => (
                  <th key={col} style={{
                    padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: S[500],
                    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
                    borderBottom: `1px solid ${S[200]}`,
                  }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scans.slice(0, 10).map((scan, i) => {
                const isCompleted = scan.status === 'completed' || scan.status === 'COMPLETED';
                return (
                  <tr key={i} className="sdp-row" style={{ borderBottom: `1px solid ${S[150]}`, transition: 'background 0.15s' }}>
                    <td style={{ padding: '9px 12px', color: S[800], fontWeight: 600 }}>{scan.engine}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: isCompleted ? ACCENT.emerald.bg : ACCENT.amber.bg,
                        color: isCompleted ? ACCENT.emerald.text : ACCENT.amber.text,
                        border: `1px solid ${isCompleted ? ACCENT.emerald.border : ACCENT.amber.border}`,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>
                        {isCompleted ? <CheckCircle size={10} /> : <Clock size={10} />}
                        {scan.status}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', color: S[700], fontFamily: "'IBM Plex Mono', monospace" }}>{scan.agentsFound}</td>
                    <td style={{ padding: '9px 12px', color: S[700], fontFamily: "'IBM Plex Mono', monospace" }}>{scan.signalsGenerated}</td>
                    <td style={{ padding: '9px 12px', color: S[500], fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{scan.duration}</td>
                    <td style={{ padding: '9px 12px', color: S[500], fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{scan.date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Detection Rules */}
      <SectionCard title="Detection Rules" accent={ACCENT.amber} actions={
        <button
          onClick={() => setShowRuleForm(!showRuleForm)}
          style={{
            ...btnBase, padding: '7px 14px', border: `1px solid ${BRAND.primary}40`,
            background: BRAND.light, color: BRAND.primary, fontSize: 12, fontWeight: 600,
          }}
        >
          <Plus size={13} />
          New Rule
        </button>
      }>
        {showRuleForm && (
          <div style={{
            marginBottom: 20, padding: 16, background: S[50], borderRadius: 10,
            border: `1px solid ${S[200]}`, animation: 'sdpSlideUp 0.2s ease',
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Engine
              </label>
              <select style={{ ...inputBase, fontSize: 12 }}>
                {Object.entries(ENGINE_META).map(([key, meta]) => (
                  <option key={key} value={key}>{meta.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Rule Name
              </label>
              <input
                type="text"
                placeholder="e.g., Detect unregistered K8s deployments"
                style={{ ...inputBase, fontSize: 12 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="sdp-btn-primary"
                style={{ ...btnBase, padding: '8px 14px', background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600 }}
              >
                Create Rule
              </button>
              <button
                onClick={() => setShowRuleForm(false)}
                className="sdp-btn-ghost"
                style={{ ...btnBase, padding: '8px 14px', background: S[100], color: S[600], fontSize: 12, fontWeight: 500 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(rules).slice(0, 20).map(([engineType, ruleList]) => {
            const meta = ENGINE_META[engineType] || { label: engineType, icon: Cpu, accent: ACCENT.slate };
            const EngineIcon = meta.icon;
            const isExpanded = expandedEngine === engineType;
            return (
              <div key={engineType}>
                <button
                  onClick={() => setExpandedEngine(isExpanded ? null : engineType)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '12px 14px', background: S[25],
                    border: `1px solid ${S[200]}`, borderRadius: isExpanded ? '10px 10px 0 0' : 10,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: 13, fontWeight: 600, color: S[800], transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EngineIcon size={14} color={meta.accent.icon} />
                    {meta.label}
                    <MetricPill value={(ruleList || []).length} label="rules" />
                  </div>
                  {isExpanded ? <ChevronUp size={15} color={S[400]} /> : <ChevronDown size={15} color={S[400]} />}
                </button>

                {isExpanded && (
                  <div style={{
                    padding: 12, background: S[50], borderRadius: '0 0 10px 10px',
                    borderLeft: `1px solid ${S[200]}`, borderRight: `1px solid ${S[200]}`,
                    borderBottom: `1px solid ${S[200]}`, animation: 'sdpSlideUp 0.15s ease',
                  }}>
                    {(ruleList || []).map((rule, i) => (
                      <div key={i} style={{
                        padding: '10px 12px', marginBottom: i < (ruleList || []).length - 1 ? 6 : 0,
                        background: '#fff', borderRadius: 8, border: `1px solid ${S[150]}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: S[800] }}>{rule.name}</div>
                          <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>
                            Severity: <span style={{
                              fontWeight: 600,
                              color: (RISK_MAP[rule.severity] || RISK_MAP.MEDIUM).text,
                            }}>{rule.severity || 'MEDIUM'}</span>
                          </div>
                        </div>
                        <button
                          className="sdp-action-btn"
                          style={{
                            ...btnBase, padding: '4px 8px', borderRadius: 6, border: `1px solid ${S[200]}`,
                            background: '#fff', color: S[500], fontSize: 11,
                          }}
                        >
                          {rule.enabled ? <ToggleLeft size={14} color={ACCENT.emerald.icon} /> : <Settings size={14} />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
};

const SignalsTab = ({ loading, data, onResolveSignal }) => {
  const [filterEngine, setFilterEngine] = useState('All');
  const [selectedSignal, setSelectedSignal] = useState(null);

  if (loading) return <TabSkeleton />;

  const signals = data || [];
  const filtered = filterEngine === 'All'
    ? signals
    : signals.filter(s => s.engineType === filterEngine);

  const engines = [...new Set(signals.map(s => s.engineType))];

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, animation: 'sdpFadeIn 0.3s ease' }}>
      {/* Filter */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center',
        padding: '10px 16px', background: '#fff', borderRadius: 10, border: `1px solid ${S[200]}`,
      }}>
        <Filter size={14} color={S[400]} />
        <select
          value={filterEngine}
          onChange={(e) => setFilterEngine(e.target.value)}
          style={{ ...inputBase, width: 'auto', padding: '7px 12px', fontSize: 12 }}
        >
          <option value="All">All Engines</option>
          {engines.map((e) => {
            const meta = ENGINE_META[e] || { label: e };
            return <option key={e} value={e}>{meta.label}</option>;
          })}
        </select>
        <MetricPill value={filtered.length} label={`of ${signals.length} signals`} icon={Radio} />
      </div>

      {/* Signals List */}
      {filtered.length === 0 ? (
        <EmptyState icon={Radio} title="No signals detected" subtitle="Discovery engines will emit signals here when anomalous agent activity is identified" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((signal) => {
            const isSelected = selectedSignal === signal.id;
            const sevAccent = signal.severity === 'CRITICAL' ? ACCENT.rose : signal.severity === 'HIGH' ? ACCENT.amber : ACCENT.violet;
            const meta = ENGINE_META[signal.engineType] || { label: signal.engineType, icon: Cpu, accent: ACCENT.slate };

            return (
              <div
                key={signal.id}
                onClick={() => setSelectedSignal(isSelected ? null : signal.id)}
                className="sdp-signal-card"
                style={{
                  padding: 16, background: '#fff', border: `1px solid ${S[200]}`,
                  borderRadius: 10, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: sevAccent.icon, opacity: 0.15 }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: sevAccent.bg, border: `1px solid ${sevAccent.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Radio size={14} color={sevAccent.icon} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>{signal.type}</div>
                      <RiskBadge tier={signal.severity} size="small" />
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11, color: S[500] }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} /> {signal.detectedTime}
                      </span>
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                      }}>
                        {signal.confidence}% confidence
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: S[600], marginTop: 4 }}>
                      Agent: <span style={{ fontWeight: 600, color: S[800] }}>{signal.agentName}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 10, fontWeight: 500, color: meta.accent.text,
                      background: meta.accent.bg, padding: '2px 7px', borderRadius: 4,
                      border: `1px solid ${meta.accent.border}`,
                    }}>
                      <meta.icon size={10} />
                      {meta.label}
                    </span>
                  </div>
                </div>

                {isSelected && (
                  <div style={{
                    marginTop: 14, paddingTop: 14, borderTop: `1px solid ${S[150]}`,
                    animation: 'sdpSlideUp 0.15s ease',
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                      letterSpacing: '0.08em', marginBottom: 8,
                    }}>
                      Evidence
                    </div>
                    <pre style={{
                      background: S[900], color: S[200], padding: 14, borderRadius: 8,
                      overflow: 'auto', maxHeight: 200, fontSize: 11,
                      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                      lineHeight: 1.5, border: `1px solid ${S[800]}`,
                    }}>
                      {JSON.stringify(signal.evidence || {}, null, 2)}
                    </pre>
                    <button
                      onClick={(e) => { e.stopPropagation(); onResolveSignal(signal.id); }}
                      className="sdp-btn-primary"
                      style={{
                        ...btnBase, marginTop: 12, padding: '8px 14px',
                        background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      <CheckCircle size={13} />
                      Resolve Signal
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   PAGE ROOT
   ══════════════════════════════════════════════════════════════════════════════ */

export default function ShadowDiscoveryPage() {
  const { addToast } = useToast();
  const { replaceContext, clearContext } = useNavigation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [agentsData, setAgentsData] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [scansRulesData, setScansRulesData] = useState(null);
  const [signalsData, setSignalsData] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);

  useEffect(() => {
    clearContext();
  }, [clearContext]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.shadowDiscoveryDashboard();
      setDashboardData(data);
    } catch (err) {
      addToast('Failed to load dashboard: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.shadowDiscoveryAgents();
      setAgentsData(data.agents || []);
    } catch (err) {
      addToast('Failed to load agents: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadGraph = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.shadowDiscoveryGraph();
      setGraphData(data);
    } catch (err) {
      addToast('Failed to load graph: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadScansRules = useCallback(async () => {
    try {
      setLoading(true);
      const [scans, rules] = await Promise.all([
        api.shadowDiscoveryScans(),
        api.shadowDiscoveryRules(),
      ]);
      setScansRulesData({ scans: scans.scans || [], rules: rules.rules || {} });
    } catch (err) {
      addToast('Failed to load scans & rules: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadSignals = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.shadowDiscoverySignals();
      setSignalsData(data.signals || []);
    } catch (err) {
      addToast('Failed to load signals: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
    else if (activeTab === 'agents') loadAgents();
    else if (activeTab === 'graph') loadGraph();
    else if (activeTab === 'scans') loadScansRules();
    else if (activeTab === 'signals') loadSignals();
  }, [activeTab, loadDashboard, loadAgents, loadGraph, loadScansRules, loadSignals]);

  const handleRunFullScan = async () => {
    try {
      // DEF-DISCOVERY-SCAN-400-001 — backend expects engineTypes: List<String>.
      // Empty array is the canonical "run every engine" payload (see
      // ShadowDiscoveryController.triggerDiscoveryScan §823). Sending the
      // string 'all' caused Jackson to reject the body as malformed JSON.
      await api.shadowDiscoveryScanTrigger({ engineTypes: [] });
      addToast('Full scan started', 'success');
      setTimeout(loadDashboard, 2000);
    } catch (err) {
      addToast('Failed to start scan: ' + err.message, 'error');
    }
  };

  const handleRunEngineScan = async (engine) => {
    try {
      await api.shadowDiscoveryScanTrigger({ engineTypes: [engine] });
      addToast(`Scan started for ${engine}`, 'success');
      setTimeout(loadScansRules, 2000);
    } catch (err) {
      addToast('Failed to start scan: ' + err.message, 'error');
    }
  };

  const handleInvestigate = async (agentId) => {
    try {
      await api.shadowDiscoveryUpdateStatus(agentId, { status: 'INVESTIGATING' });
      addToast('Agent marked as investigating', 'success');
      loadAgents();
    } catch (err) {
      addToast('Failed to update status: ' + err.message, 'error');
    }
  };

  const handleEnroll = async (agentId) => {
    try {
      await api.shadowDiscoveryEnroll(agentId, {});
      addToast('Agent enrolled successfully', 'success');
      loadAgents();
    } catch (err) {
      addToast('Failed to enroll agent: ' + err.message, 'error');
    }
  };

  const handleDismiss = async (agentId) => {
    try {
      await api.shadowDiscoveryDismiss(agentId, { reason: 'user_dismissed' });
      addToast('Agent dismissed', 'success');
      loadAgents();
    } catch (err) {
      addToast('Failed to dismiss agent: ' + err.message, 'error');
    }
  };

  const handleBulkStatusChange = async (status) => {
    try {
      await api.shadowDiscoveryBulkStatus({ agentIds: selectedAgents, status });
      addToast(`${selectedAgents.length} agents updated to ${status}`, 'success');
      setSelectedAgents([]);
      loadAgents();
    } catch (err) {
      addToast('Failed to update agents: ' + err.message, 'error');
    }
  };

  const handleBulkDismiss = async () => {
    try {
      await api.shadowDiscoveryBulkStatus({ agentIds: selectedAgents, status: 'DISMISSED' });
      addToast(`${selectedAgents.length} agents dismissed`, 'success');
      setSelectedAgents([]);
      loadAgents();
    } catch (err) {
      addToast('Failed to dismiss agents: ' + err.message, 'error');
    }
  };

  const handleResolveSignal = async (signalId) => {
    try {
      await api.shadowDiscoveryResolveSignal(signalId);
      addToast('Signal resolved', 'success');
      loadSignals();
    } catch (err) {
      addToast('Failed to resolve signal: ' + err.message, 'error');
    }
  };

  const activeTabMeta = TABS.find(t => t.key === activeTab);
  /* G100 — v2 TabNav config */
  const v2Tabs = TABS.map(t => ({ id: t.key, label: t.label }));

  return (
    <V2PageShell
      title="Agent Discovery"
      subtitle="Discover and protect shadow agents across your enterprise."
      tags={<V2Pill tone="trust" size="sm">Discovery</V2Pill>}
    >
      <GlobalStyles />

      <V2TabNav
        tabs={v2Tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Agent Discovery sections"
      />

      {/* Active tab description (kept from v1) */}
      {activeTabMeta && (
        <div className="px-3 py-2 mt-3 mb-4 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary flex items-center gap-2">
          {React.createElement(activeTabMeta.icon, { size: 12 })}
          <span>{activeTabMeta.desc}</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="overflow-y-auto">
        {activeTab === 'dashboard' && (
          <DashboardTab loading={loading} data={dashboardData} onRunScan={handleRunFullScan} />
        )}
        {activeTab === 'agents' && (
          <AgentsTab
            loading={loading}
            data={agentsData}
            selectedAgents={selectedAgents}
            onSelectAgent={setSelectedAgents}
            onInvestigate={handleInvestigate}
            onEnroll={handleEnroll}
            onDismiss={handleDismiss}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkDismiss={handleBulkDismiss}
          />
        )}
        {activeTab === 'graph' && (
          <GraphTab loading={loading} data={graphData} />
        )}
        {activeTab === 'scans' && (
          <ScansRulesTab
            loading={loading}
            data={scansRulesData}
            onRunFullScan={handleRunFullScan}
            onRunEngineScan={handleRunEngineScan}
            onCreateRule={() => addToast('Rule creation modal would open', 'info')}
          />
        )}
        {activeTab === 'signals' && (
          <SignalsTab loading={loading} data={signalsData} onResolveSignal={handleResolveSignal} />
        )}
      </div>
    </V2PageShell>
  );
}
